import React, { useContext } from "react";
import {
    Badge,
    BadgeColor,
    createListTableColumns,
    ensure,
    ListTableColumn,
    renderDate,
    renderDateTime,
    renderName,
    Stack,
    TableColumn
} from "sprint-common-web";

import { OnPremOrderSource } from "../../../common/api/orders-api/on-prem-order/GetOnPremOrderListResponse";
import { RecordType } from "../../../common/types/RecordType";
import { AssetsAvailabilityList } from "../../common/components/listings/AssetsAvailabilityList";
import { OrderCompletionProgressInfo } from "../../common/components/listings/OrderCompletionProgressInfo";
import { renderContent } from "../../common/components/listings/renderContent";
import { renderOrderResolutions } from "../../common/components/listings/renderOrderResolutions";
import { renderRuntime } from "../../common/components/listings/renderRuntime";
import { renderTargetFilenames } from "../../common/components/listings/renderTargetFilenames";
import { VideoInfo } from "../../common/components/listings/VideoInfo";
import { OrderAssetStorageType } from "../../common/types/OrderAssetStorageType";
import { calculateOrderProgress } from "../../common/utils/calculateOrderProgress";
import { getOnPremOrderDashboardHref } from "../../OrdersRoutes";
import { mapOnPremOrderStatusName } from "../common/utils/getAvailableOnPremOrderStatuses";
import { resolveOnPremOrderSourceBadgeColor } from "../common/utils/resolveOnPremOrderSourceBadgeColor";
import { OnPremOrderActionsListingButton } from "./actions/OnPremOrderActionsListingButton";
import { PinOnPremOrderListingButton } from "./buttons/PinOnPremOrderListingButton";
import { getSortedOrderColumns, OnPremOrderColumn, OnPremOrderListingConfig } from "./config/OnPremOrderListingConfig";
import { OnPremOrderListingContext } from "./context/OnPremOrderListingContext";
import { OnPremOrderListingItem } from "./OnPremOrderListingItem";

type OnPremOrderItem = OnPremOrderListingItem & { info: string; progress: string; resolution: string };

type OnPremOrderListingColumn = TableColumn<OnPremOrderItem>;

export const createOnPremOrderListingColumns = (): ListTableColumn<OnPremOrderListingItem>[] => {
    const { listingConfig } = useContext(OnPremOrderListingContext);
    const columns = getListingColumns(listingConfig);

    return createListTableColumns<OnPremOrderItem>(columns);
};

const getListingColumns = (config: OnPremOrderListingConfig): OnPremOrderListingColumn[] => {
    const columnMapping: Record<OnPremOrderColumn, OnPremOrderListingColumn> = {
        [OnPremOrderColumn.PoNumber]: {
            type: "link",
            key: "pONumber",
            name: "PO Number",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            isSortable: true,
            getHref: (item: OnPremOrderListingItem) => getOnPremOrderDashboardHref(item.id)
        },
        [OnPremOrderColumn.Airline]: {
            type: "basic",
            key: "airline",
            name: "Airline",
            minWidth: 100,
            maxWidth: 180,
            isResizable: true,
            isSortable: true,
            onRender: renderName
        },
        [OnPremOrderColumn.System]: {
            type: "basic",
            key: "system",
            name: "System",
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            isSortable: true,
            onRender: renderName
        },
        [OnPremOrderColumn.StartDate]: {
            type: "basic",
            key: "startDate",
            name: "Start Date",
            minWidth: 90,
            maxWidth: 90,
            isResizable: true,
            isSortable: true,
            onRender: renderDate
        },

        [OnPremOrderColumn.DueDate]: {
            type: "basic",
            key: "dueDate",
            name: "Due Date",
            minWidth: 90,
            maxWidth: 90,
            isResizable: true,
            isSortable: true,
            onRender: renderDate
        },
        [OnPremOrderColumn.Progress]: {
            type: "basic",
            key: "progress",
            name: "Progress",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            isSortable: true,
            onRender: renderOrderProgress
        },
        [OnPremOrderColumn.Info]: {
            type: "basic",
            key: "info",
            name: "Info",
            minWidth: 80,
            maxWidth: 130,
            isResizable: true,
            onRender: renderInfo
        },
        [OnPremOrderColumn.Source]: {
            type: "basic",
            key: "source",
            name: "Source",
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: renderSource
        },
        [OnPremOrderColumn.TargetFilenames]: {
            type: "basic",
            key: "targetFilenames",
            name: "Target filenames",
            minWidth: 120,
            maxWidth: 200,
            isResizable: true,
            onRender: renderTargetFilenames
        },
        [OnPremOrderColumn.Resolution]: {
            type: "basic",
            key: "resolution",
            name: "Resolution",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: (item) =>
                renderOrderResolutions({
                    targetResolution: item?.targetResolution,
                    sourceResolution: item?.video.resolution
                })
        },
        [OnPremOrderColumn.Video]: {
            type: "basic",
            key: "video",
            name: "Video",
            minWidth: 40,
            maxWidth: 40,
            isResizable: true,
            onRender: renderVideoAvailability
        },
        [OnPremOrderColumn.Runtime]: {
            type: "basic",
            key: "runtime",
            name: "Runtime",
            minWidth: 60,
            maxWidth: 80,
            onRender: renderRuntime
        },
        [OnPremOrderColumn.Soundtracks]: {
            type: "basic",
            key: "soundtracks",
            name: "Soundtracks",
            minWidth: 120,
            maxWidth: 160,
            isResizable: true,
            onRender: renderSoundtracksAvailabilityList
        },
        [OnPremOrderColumn.Subtitles]: {
            type: "basic",
            key: "subtitles",
            name: "Subtitles",
            minWidth: 120,
            maxWidth: 160,
            isResizable: true,
            onRender: renderSubtitlesAvailabilityList
        },
        [OnPremOrderColumn.Status]: {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: renderOnPremOrderStatus
        },
        [OnPremOrderColumn.CreatedDate]: {
            type: "basic",
            key: "createdDate",
            name: "Created",
            minWidth: 150,
            maxWidth: 150,
            isSortable: true,
            isResizable: true,
            onRender: renderDateTime
        },
        [OnPremOrderColumn.Content]: {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 100,
            maxWidth: 400,
            isResizable: true,
            isSortable: true,
            onRender: renderContent
        },
        [OnPremOrderColumn.Actions]: {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            buttons: [
                {
                    id: "actions",
                    render: OnPremOrderActionsListingButton
                },
                {
                    id: "pin",
                    render: PinOnPremOrderListingButton
                }
            ]
        }
    };

    const columns: OnPremOrderListingColumn[] = [];

    getSortedOrderColumns(config.columns).forEach(([key, column]) => {
        const resolvedKey = Number(key) as OnPremOrderColumn;

        if (column.visible && columnMapping[resolvedKey]) {
            columns.push(columnMapping[resolvedKey]);
        }
    });

    return columns;
};

const renderVideoAvailability = (item?: OnPremOrderListingItem): JSX.Element => {
    ensure(item);

    return (
        <VideoInfo
            availability={item.video.availability}
            requestedAspectRatio={item.video.aspectRatio}
            type={OrderAssetStorageType.OnPrem}
            requestedResolution={item.targetResolution}
            nonMatchingVideos={item.video.nonMatchingVideos}
            testId={"order-onprem-video-availability-status"}
        />
    );
};

const renderSoundtracksAvailabilityList = (item?: OnPremOrderListingItem): JSX.Element => {
    ensure(item);

    return (
        <AssetsAvailabilityList
            items={item.soundtracks}
            type={OrderAssetStorageType.OnPrem}
            testId={"order-onprem-soundtracks-availability-status"}
            assetType={RecordType.Audio}
        />
    );
};

const renderSubtitlesAvailabilityList = (item?: OnPremOrderListingItem): JSX.Element => {
    ensure(item);

    return (
        <AssetsAvailabilityList
            items={item.subtitles}
            type={OrderAssetStorageType.OnPrem}
            testId={"order-onprem-subtitles-availability-status"}
            assetType={RecordType.Subtitle}
        />
    );
};

const renderOrderProgress = (item?: OnPremOrderListingItem): JSX.Element => {
    ensure(item);

    const progress = calculateOrderProgress(
        item.video.availability,
        item.soundtracks.map((s) => s.availability),
        item.subtitles.map((s) => s.availability)
    );

    return (
        <OrderCompletionProgressInfo
            progress={progress}
            key={`${item.id}-onprem-progress`}
            testId="order-onprem-completion-progress"
        />
    );
};

const renderOnPremOrderStatus = (item?: OnPremOrderListingItem): JSX.Element => {
    ensure(item);
    return <>{mapOnPremOrderStatusName(item.status)}</>;
};

const renderInfo = (item?: OnPremOrderListingItem): JSX.Element => {
    ensure(item);

    return (
        <Stack
            tokens={{ childrenGap: 6 }}
            horizontal={false}
            styles={{ root: { display: "flex", flexDirection: "column", alignItems: "flex-start" } }}
        >
            {!!item.isTrailer && <Badge text="Trailer" color={BadgeColor.ApplicationPrimary} />}
            {!!item.isRevised && <Badge text="Revised" color={BadgeColor.LightBlue} />}
        </Stack>
    );
};

const renderSource = (item?: OnPremOrderListingItem) => {
    ensure(item);

    const badgeColor = resolveOnPremOrderSourceBadgeColor(item.source);

    return <Badge color={badgeColor} text={OnPremOrderSource[item.source]} />;
};
